import { displayValueText, formatInteractionFieldValue } from './interaction-note-format';

/**
 * Making already-written job notes readable.
 *
 * Notes persisted before interaction answers were formatted properly contain the
 * raw material: JSON objects, whole serialised interaction forms, and complete
 * `data:image/png;base64,…` signature strings. Those records cannot be repaired by
 * changing what we submit from here — the text is already in the database — so they
 * are tidied at render time instead. Nothing is rewritten in storage.
 *
 * The note is split into segments so an embedded blob can become a thumbnail while
 * the surrounding prose keeps its existing HTML handling.
 */

export type NoteSegment = { kind: 'text'; text: string } | { kind: 'image'; src: string };

/**
 * A base64 image payload sitting in the note as bare text.
 *
 * Deliberately limited to raster formats. Note text is user-authored, and while an
 * SVG loaded through `<img>` cannot execute script, there is no reason for a
 * signature pad to produce one — so `svg+xml` is left as text rather than promoted
 * into an element. The character class also confines the payload to the base64
 * alphabet, so a matched value can never carry quotes or another scheme.
 */
const BARE_IMAGE_DATA_URI = /data:image\/(?!svg)[a-z0-9.+-]+;base64,[A-Za-z0-9+/]+=*/gi;

/**
 * Long enough that we only collapse an actual payload. A short data URI is more
 * likely a placeholder or a test fixture, and leaving it as text is harmless.
 */
const MIN_BLOB_LENGTH = 64;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Fields of a serialised interaction form, wherever they sit in its containers. */
const formFields = (value: Record<string, unknown>): Array<Record<string, unknown>> => {
  const containers = Array.isArray(value.containers) ? value.containers : [];
  return containers.flatMap((container) =>
    isRecord(container) && Array.isArray(container.fields) ? container.fields.filter(isRecord) : []
  );
};

/**
 * Describe a JSON payload found in note text as a single readable line.
 *
 * A serialised interaction form becomes its answers (`Fault: Scanner · Qty: 2`),
 * reusing the same per-type formatting the history panel uses, so a signature in
 * there is described rather than dumped. Anything else falls back to its most
 * name-like property.
 */
export const describeInteractionPayload = (value: unknown): string | undefined => {
  if (!isRecord(value)) return undefined;

  const fields = formFields(value);
  if (fields.length > 0) {
    const parts = fields
      .map((field) => ({
        title: typeof field.title === 'string' ? field.title : '',
        display: formatInteractionFieldValue(field as { type?: string; value?: unknown }),
      }))
      .map(({ title, display }) => {
        if (display.kind === 'omitted') return undefined;
        const shown = display.kind === 'image' ? '[signature]' : displayValueText(display);
        if (!shown) return undefined;
        return title ? `${title}: ${shown}` : shown;
      })
      .filter((part): part is string => !!part);
    if (parts.length > 0) {
      const title = typeof value.title === 'string' && value.title ? `${value.title} — ` : '';
      return `${title}${parts.join(' · ')}`;
    }
  }

  const display = formatInteractionFieldValue({ value });
  return display.kind === 'text' ? display.text : undefined;
};

/**
 * Find the JSON object starting at `open`, respecting braces inside strings, and
 * return its end index (exclusive) or -1 if it never closes.
 */
const findObjectEnd = (text: string, open: number): number => {
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = open; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      if (depth === 0) return i + 1;
    }
  }
  return -1;
};

/**
 * Replace JSON objects embedded in note text with a readable description. Text that
 * merely happens to contain a brace is left alone — only substrings that actually
 * parse as a JSON object are touched.
 */
export const collapseJsonInNote = (note: string): string => {
  if (!note.includes('{')) return note;

  let out = '';
  let i = 0;
  while (i < note.length) {
    if (note[i] !== '{') {
      out += note[i];
      i++;
      continue;
    }
    const end = findObjectEnd(note, i);
    if (end === -1) {
      out += note.slice(i);
      break;
    }
    const candidate = note.slice(i, end);
    let described: string | undefined;
    try {
      described = describeInteractionPayload(JSON.parse(candidate));
    } catch {
      described = undefined;
    }
    out += described ?? candidate;
    i = end;
  }
  return out;
};

/**
 * Split note text into renderable segments, pulling embedded base64 images out so
 * they can render as thumbnails instead of pages of text, and collapsing any JSON
 * left in the prose.
 *
 * Returns a single text segment for the overwhelming majority of notes, which have
 * neither problem.
 */
export const tidyNoteText = (note: string): NoteSegment[] => {
  if (!note) return [];

  const segments: NoteSegment[] = [];
  let lastIndex = 0;

  // `matchAll` needs the regex reset between calls because it carries /g state.
  BARE_IMAGE_DATA_URI.lastIndex = 0;
  for (const match of note.matchAll(BARE_IMAGE_DATA_URI)) {
    const src = match[0];
    const start = match.index ?? 0;
    if (src.length < MIN_BLOB_LENGTH) continue;

    const before = note.slice(lastIndex, start);
    if (before.trim() !== '') segments.push({ kind: 'text', text: collapseJsonInNote(before) });
    segments.push({ kind: 'image', src });
    lastIndex = start + src.length;
  }

  const rest = note.slice(lastIndex);
  if (segments.length === 0) return [{ kind: 'text', text: collapseJsonInNote(note) }];
  if (rest.trim() !== '') segments.push({ kind: 'text', text: collapseJsonInNote(rest) });
  return segments;
};
